import React, { useState, useMemo, useEffect } from 'react';
import { FlyoutContainer, Dropdown } from '@cmhc-schl/hearth-ui';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ErrorMessage, { SpinnerElement } from '../shared/ErrorMessage';
import HbtMasterPoolPreimumsBreakdownTable from './components/HbtMasterPoolPreimumsBreakdownTable';
import HbtMasterPoolPremiumsAndTaxesSection from './components/HbtMasterPoolPremiumsAndTaxesSection';
import styles from './styles.module.scss';
import type {
  PortfolioPoolBreakdown,
  MasterPoolPremiumsAndTaxes,
  HbtMasterPoolPremiumsAndTaxesProps
} from './types';

const HbtMasterPoolPremiumsAndTaxes = ({
  shouldShowPremiumsAndTaxes,
  noDataAvailable,
  premiumAndTaxesData,
  premiumAndTaxesDataLoading: isLoading,
  premiumAndTaxesError: isError
}: HbtMasterPoolPremiumsAndTaxesProps) => {
  const [openFlyout, setOpenFlyout] = useState<boolean>(false);
  const [selectedDetails, setSelectedDetails] = useState<
    PortfolioPoolBreakdown | MasterPoolPremiumsAndTaxes | null | undefined
  >(null);
  const { t } = useTranslation('HbtMasterPool');
  const [masterPremiumsBreakdownData, setMasterPremiumsBreakdownData] =
    useState<MasterPoolPremiumsAndTaxes | null>(null);
  const toggleDrawer = (newOpen: boolean) => {
    setOpenFlyout(newOpen);
  };

  useEffect(() => {
    if (Array.isArray(premiumAndTaxesData) && premiumAndTaxesData.length > 0) {
      setMasterPremiumsBreakdownData(premiumAndTaxesData[0]);
    }
  }, [premiumAndTaxesData]);

  const dropdownOptions = useMemo(() => {
    if (!masterPremiumsBreakdownData) return [];

    const masterOption = {
      label: `${t('PI-Master-Pool-Premiums-Taxes-Flyout-Masterpool-Number')} ${String(
        masterPremiumsBreakdownData.masterPoolCmhcAccountNumber
      )}`,
      value: String(masterPremiumsBreakdownData.masterPoolCmhcAccountNumber)
    };

    const breakdownOptions =
      masterPremiumsBreakdownData.portfolioPoolBreakdown?.map((item) => ({
        label: `${String(item.poolCmhcAccountNumber)} ${
          item.unbalanced ? `(${t('PI-Master-Pool-Premiums-Taxes-Section-Unbalanced')})` : ''
        }`,
        value: String(item.poolCmhcAccountNumber)
      })) ?? [];

    const subpoolGroup =
      breakdownOptions.length > 1
        ? t('PI-Master-Pool-Premiums-Taxes-Flyout-Subpools')
        : t('PI-Master-Pool-Premiums-Taxes-Flyout-Subpool');

    return [
      {
        groupName: t('PI-Master-Pool-Premiums-Taxes-Flyout-Masterpool'),
        options: [masterOption]
      },
      {
        groupName: subpoolGroup,
        options: [...breakdownOptions]
      }
    ];
  }, [masterPremiumsBreakdownData]);

  const handleSelect = (e: SelectChangeEvent<string>) => {
    const selectedValue = e.target.value;

    if (selectedValue === String(masterPremiumsBreakdownData?.masterPoolCmhcAccountNumber)) {
      setSelectedDetails(masterPremiumsBreakdownData);
    } else {
      const matched = masterPremiumsBreakdownData?.portfolioPoolBreakdown.find(
        (item: { poolCmhcAccountNumber: number }) =>
          String(item.poolCmhcAccountNumber) === selectedValue
      );
      setSelectedDetails(matched);
    }
  };

  useEffect(() => {
    if (dropdownOptions.length > 0) {
      const defaultValue = dropdownOptions[0].options[0].value;
      handleSelect({ target: { value: defaultValue } } as SelectChangeEvent<string>);
    }
  }, [dropdownOptions]);

  return (
    <>
      {masterPremiumsBreakdownData && (
        <HbtMasterPoolPremiumsAndTaxesSection
          data={masterPremiumsBreakdownData}
          toggleDrawer={toggleDrawer}
          shouldShowPremiumsAndTaxes={shouldShowPremiumsAndTaxes}
          noDataAvailable={noDataAvailable}
          isError={isError}
          isLoading={isLoading}
        />
      )}

      <FlyoutContainer
        id="portfolio-filter-flyout-container"
        isHeaderRequired={true}
        headerText={t('PI-Master-Pool-Premiums-Taxes-Section-Premium-And-Taxes-Breakdown')}
        open={openFlyout}
        onClose={() => {
          toggleDrawer(false);
        }}
        content={
          <Box className={styles.premiumsAndTaxesFlyoutContent}>
            <Typography>{t('PI-Master-Pool-Premiums-Taxes-Flyout-Subheader')}</Typography>
            {isLoading && <SpinnerElement />}
            {isError && (
              <ErrorMessage
                errorType={'api_error'}
                errorHeader={t('PI-Master-Pool-Premiums-Taxes-API-Error-Header')}
                errorContent={t('PI-Master-Pool-Premiums-Taxes-API-Error-Content')}
                serviceNowRequired
              />
            )}
            {masterPremiumsBreakdownData && (
              <>
                <Box className={styles.premiumsAndTaxesFlyoutDropdownContent}>
                  <Typography>
                    {t('PI-Master-Pool-Premiums-Taxes-Flyout-Dropdown-Header')}
                  </Typography>
                  <Dropdown
                    placeholderText={t('Select')}
                    options={dropdownOptions}
                    onChange={(e) => handleSelect(e)}
                    initialOption={
                      String(masterPremiumsBreakdownData?.masterPoolCmhcAccountNumber) ?? ''
                    }
                    isIndented={true}
                  />
                </Box>
                <HbtMasterPoolPreimumsBreakdownTable
                  selectedDetails={selectedDetails ?? masterPremiumsBreakdownData}
                />
              </>
            )}
          </Box>
        }
      />
    </>
  );
};

export default HbtMasterPoolPremiumsAndTaxes;
